<template>
  <div class="pagination-footer">
    <ul class="pagination-info">
      <li>
        <h3>Total items: {{ total }}</h3>
      </li>
      <li>
        <div class="footer-right">
          <h4>Items per page: {{ per_page }}</h4>
          <h4>Total pages: {{ totalPages }}</h4>
          <nav aria-label="Page navigation example">
            <ul class="pagination">
              <!-- Previous Button -->
              <li
                class="page-item"
                :class="{ disabled: current_page === 1 || loadingPageButton === current_page - 1 }"
                @click="goToPage(current_page - 1)"
              >
                <span class="page-link cursor-pointer" aria-label="Previous">
                  <span v-if="loadingPageButton === current_page" class="loader-in-btn"></span>
                  <span v-else aria-hidden="true">&laquo;</span>
                  <span class="sr-only">Previous</span>
                </span>
                
              </li>

              <!-- Page Numbers -->
              <template v-if="totalPages <= 10">
                <li
                  v-for="index in totalPages"
                  :key="index"
                  class="page-item"
                  :class="{ active: current_page === index, disabled: loadingPageButton === index }"
                  @click="goToPage(index)"
                >
                  <span class="page-link cursor-pointer">
                    <span v-if="loadingPageButton === index" class="loader-in-btn"></span>
                    <span v-else>{{ index }}</span>
                  </span>
                </li>
              </template>
              <template v-else>
                <!-- First 3 pages -->
                <li
                  v-for="index in 3"
                  :key="'start-' + index"
                  class="page-item"
                  :class="{ active: current_page === index, disabled: loadingPageButton === index }"
                  @click="goToPage(index)"
                >
                  <span class="page-link cursor-pointer">
                    <span v-if="loadingPageButton === index" class="loader-in-btn"></span>
                    <span v-else>{{ index }}</span>
                  </span>
                </li>
                <!-- Dots after first 3 pages if needed -->
                <li v-if="current_page > 5" class="page-item disabled">
                  <span class="page-link">...</span>
                </li>
                <!-- Pages around current page -->
                <template v-for="index in middlePages" :key="'middle-' + index">
                  <li
                    class="page-item"
                    :class="{ active: current_page === index, disabled: loadingPageButton === index }"
                    @click="goToPage(index)"
                  >
                    <span class="page-link cursor-pointer">
                      <span v-if="loadingPageButton === index" class="loader-in-btn"></span>
                      <span v-else>{{ index }}</span>
                    </span>
                  </li>
                </template>
                <!-- Dots before last 3 pages if needed -->
                <li v-if="current_page < totalPages - 4" class="page-item disabled">
                  <span class="page-link">...</span>
                </li>
                <!-- Last 3 pages -->
                <li
                  v-for="index in [totalPages-2, totalPages-1, totalPages]"
                  :key="'end-' + index"
                  v-if="index > 3"
                  class="page-item"
                  :class="{ active: current_page === index, disabled: loadingPageButton === index }"
                  @click="goToPage(index)"
                >
                  <span class="page-link cursor-pointer">
                    <span v-if="loadingPageButton === index" class="loader-in-btn"></span>
                    <span v-else>{{ index }}</span>
                  </span>
                </li>
              </template>

              <!-- Next Button -->
              <li
                class="page-item"
                :class="{ disabled: current_page === totalPages || loadingPageButton === current_page + 1 }"
                @click="goToPage(current_page + 1)"
              >
                <span class="page-link cursor-pointer" aria-label="Next">
                  <span v-if="loadingPageButton === current_page + 1" class="loader-in-btn"></span>
                  <span v-else aria-hidden="true">&raquo;</span>
                  <span class="sr-only">Next</span>
                </span>
              </li>
            </ul>
          </nav>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "Pagination",
  props: {
    current_page: {
      type: Number,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    per_page: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    loadingPageButton: {
      type: Number,
      required: true,
    },
  },
  emits: ["page-change"],
  methods: {
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.current_page) return;
      this.$emit("page-change", page);
    },
  },
};
</script>

<style scoped>
.pagination-footer {
    position: fixed;
    left: 0;
    bottom: 55px;
    width: 100vw;
    background: #ebe7e7;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, .07);
    z-index: 100;
    padding: 10px 3rem 10px 20rem;
    display: flex;
    justify-content: center;
}

.pagination-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 30px;
}

.footer-right h4 {
  margin: 0 10px 0 0;
  font-size: 16px;
  font-weight: 500;
}

.pagination {
  display: flex;
  align-items: center;
  margin: 0 0 0 20px;
  padding: 0;
  list-style: none;
}

.page-item {
  margin: 0 3px;
  cursor: pointer;
  user-select: none;
}

.page-item.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.page-item.active .page-link {
  background: #ff8400;
  color: #fff;
  border-radius: 4px;
}

.page-link {
  display: block;
  padding: 6px 12px;
  color: #F16E5E;
  background: transparent;
  border: none;
  font-size: 16px;
  transition: background 0.2s, color 0.2s;
}

.page-link.cursor-pointer {
  cursor: pointer;
}

.page-link:hover {
  background: #e9ecef;
  color: #e63b1f80 !important;
  border-radius: 4px;
}

@media (max-width: 600px) {
  .pagination-info {
    flex-direction: column;
    gap: 10px;
  }
  .footer-right {
    flex-direction: column;
    gap: 10px;
  }
}

.loader-in-btn {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ccc;
  border-top: 2px solid #e63b1f80;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  vertical-align: middle;
}

@keyframes spin {
    0% { transform: rotate(0deg);}
    100% { transform: rotate(360deg);}
  }
</style>
